// src/components/chat/sections/EventOutfit.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../../styles/components/_event-description.css';
import '../../../styles/components/_messages.css';
import '../../../styles/components/_generate-outfit.css';
import '../../../styles/components/_outfit-result.css';
import {
    FacebookShareButton,
    TwitterShareButton,
    WhatsappShareButton,
    PinterestShareButton,
    TelegramShareButton,
    FacebookIcon,
    TwitterIcon,
    WhatsappIcon,
    PinterestIcon,
    TelegramIcon
} from 'react-share';

const EventOutfit = ({ messages = [], profileData, addMessage }) => {
    const [eventDescription, setEventDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [outfitData, setOutfitData] = useState(null);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    // Отладочное сообщение для проверки props
    useEffect(() => {
        console.log('EventOutfit component mounted with props:', { 
            hasMessages: Array.isArray(messages),
            messagesCount: Array.isArray(messages) ? messages.length : 0,
            hasProfileData: !!profileData,
            addMessageType: typeof addMessage
        });
    }, [messages, profileData, addMessage]);

    // Безопасная обертка для функции addMessage
    const safeAddMessage = (text, sender, section = 'event') => {
        console.log('Trying to add message:', { text, sender, section });
        
        if (typeof addMessage === 'function') {
            addMessage(text, sender, section);
        } else {
            console.warn('addMessage is not available, adding directly to state');
            // Добавляем сообщение напрямую (как запасной вариант)
            if (sender === 'bot') {
                setOutfitData(prev => ({
                    ...prev,
                    botMessage: text
                }));
            }
        }
    };

    // Примеры событий
    const examples = [
        'Wedding ceremony - formal',
        'Beach party - casual',
        'Business meeting',
        'Date night',
        'Birthday celebration',
        'Outdoor festival'
    ];

    // Функция для выбора примера
    const handleExampleClick = (example) => {
        setEventDescription(example);
    };

    // Функция сохранения образа
    const saveOutfit = async () => {
        if (!outfitData || !profileData || !profileData.email) {
            console.error('Missing data for saving outfit');
            return;
        }

        setIsSaving(true);

        try {
            const explanation = extractExplanation(outfitData.aiMessage) || 
                              outfitData.explanation || 
                              'Event outfit suggestion';
            
            // Подготовка данных для сохранения
            const lookData = {
                look_img: outfitData.collagePath,
                look_description: `${eventDescription}: ${explanation}`,
                look_date: new Date().toISOString(),
                look_products: JSON.stringify(Object.keys(outfitData.productLinks || {})),
                products_links: JSON.stringify(outfitData.productLinks || {}),
                email: profileData.email,
                event_description: eventDescription
            };

            // Отправляем запрос на сохранение образа
            await axios.post('/saveLook', lookData);
            
            setIsSaved(true);
            safeAddMessage('✅ Outfit saved to your collection!', 'bot');
            
            // Через 3 секунды убираем индикатор сохранения
            setTimeout(() => {
                setIsSaved(false);
            }, 3000);
            
        } catch (error) {
            console.error('Error saving outfit:', error);
            safeAddMessage('❌ Sorry, there was a problem saving your outfit.', 'bot');
        } finally {
            setIsSaving(false);
        }
    };

    // Обработчик отправки формы
    const handleSubmit = async () => {
        if (!eventDescription.trim()) {
            safeAddMessage("Please describe your event to get recommendations.", "bot");
            return;
        }

        setIsLoading(true);
        setError(null);
        setSubmitted(true);
        setIsSaved(false);
        
        try {
            console.log('Submitting event description:', eventDescription);
            
            // Добавляем описание в историю
            safeAddMessage(eventDescription, 'user');
            
            // Проверяем наличие данных профиля
            if (!profileData) {
                throw new Error('Profile data is missing. Please complete your profile first.');
            }
            
            const { gender, style, colors, city } = profileData;
            
            if (!gender || !style || !colors) {
                throw new Error('Your profile is incomplete. Please set your gender, style, and color preferences.');
            }
            
            console.log('Requesting outfit with params:', { gender, style, colors, city, eventDescription });
            
            // Запрос к API
            const response = await axios.post('/getEventOutfitSuggestion', {
                gender,
                style,
                colors,
                city,
                eventDescription
            });
            
            console.log('Received response:', response.data);
            
            // Сохраняем данные
            setOutfitData(response.data);
            
            // Добавляем результат в историю чата
            const explanation = response.data.explanation || 
                               extractExplanation(response.data.aiMessage) || 
                               'Here\'s your outfit for the event!';
            
            safeAddMessage(explanation, 'bot');
            
        } catch (error) {
            console.error('Error fetching event outfit:', error);
            
            let errorMessage = 'Sorry, I couldn\'t create an outfit for you right now.';
            
            if (error.response) {
                if (error.response.status === 404) {
                    errorMessage = 'I couldn\'t find suitable products for this event. Please try a different description.';
                } else {
                    errorMessage = error.response.data || errorMessage;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setError(errorMessage);
            safeAddMessage(errorMessage, 'bot');
            
        } finally {
            setIsLoading(false);
        }
    };

    // Извлекает пояснение из ответа AI
    const extractExplanation = (aiMessage) => {
        if (!aiMessage) return null;
        
        const summaryMatch = aiMessage.match(/### Summary for the whole outfit:([\s\S]*?)($|###)/);
        if (summaryMatch && summaryMatch[1]) {
            return summaryMatch[1].trim();
        }
        return null;
    };

    // Функция для отображения сообщений
    const renderMessages = () => {
        if (!Array.isArray(messages) || messages.length === 0) {
            return (
                <div className="message app-message">
                    🎉 Welcome to the Event Outfit section! Describe your event, and I'll suggest an outfit!
                </div>
            );
        }
        
        return messages.map((msg, index) => (
            <div key={index} className={`message ${msg.sender === 'user' ? 'user-message' : 'app-message'}`}>
                {msg.component ? (
                    <div dangerouslySetInnerHTML={{ __html: msg.component }} />
                ) : (
                    msg.text
                )}
            </div>
        ));
    };

    // Функция для отображения кнопок продуктов
    const renderProductButtons = () => {
        if (!outfitData || !outfitData.productLinks || Object.keys(outfitData.productLinks).length === 0) {
            return null;
        }
        
        return Object.entries(outfitData.productLinks).map(([name, link]) => (
            <a 
                key={name} 
                href={link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="product-link-button"
            >
                {name}
            </a>
        ));
    };

    // Отображение компонента с результатом
    const OutfitResultComponent = () => {
        if (!outfitData) return null;
        
        const { aiMessage, collagePath } = outfitData;
        
        const explanation = extractExplanation(aiMessage) || 
                           outfitData.explanation || 
                           'Custom outfit for your event';

        // Базовый URL для создания полного пути к изображению
        const baseUrl = window.location.origin;
        const fullImageUrl = `${baseUrl}${collagePath}`;
        
        return (
            <div className="outfit-result">
                <img src={collagePath} alt="Event Outfit" className="outfit-collage" />
                
                <div className="outfit-actions">
                    <div className="share-buttons">
                        <FacebookShareButton url={fullImageUrl} quote={explanation}>
                            <FacebookIcon size={32} round={true} />
                        </FacebookShareButton>
                        
                        <TwitterShareButton url={fullImageUrl} title={explanation}>
                            <TwitterIcon size={32} round={true} />
                        </TwitterShareButton>
                        
                        <WhatsappShareButton url={fullImageUrl} title={explanation}>
                            <WhatsappIcon size={32} round={true} />
                        </WhatsappShareButton>
                        
                        <PinterestShareButton url={fullImageUrl} media={fullImageUrl} description={explanation}>
                            <PinterestIcon size={32} round={true} />
                        </PinterestShareButton>
                        
                        <TelegramShareButton url={fullImageUrl} title={explanation}>
                            <TelegramIcon size={32} round={true} />
                        </TelegramShareButton>
                    </div>
                    
                    <button 
                        className={`save-outfit-button ${isSaved ? 'saved' : ''} ${isSaving ? 'saving' : ''}`}
                        onClick={saveOutfit}
                        disabled={isSaving || isSaved}
                    >
                        {isSaved ? '✓ Saved' : isSaving ? 'Saving...' : '💾 Save Outfit'}
                    </button>
                </div>
                
                <div className="outfit-explanation">{explanation}</div>
                
                <div className="product-links">
                    {renderProductButtons()}
                </div>
            </div>
        );
    };

    return (
        <div className="event-outfit-section">
            <div className="messages-container">
                <div className="messages">
                    {renderMessages()}
                    
                    {outfitData && !isLoading && (
                        <div className="message app-message">
                            <OutfitResultComponent />
                        </div>
                    )}
                    
                    {isLoading && (
                        <div className="message app-message">
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Creating your perfect outfit...</p>
                            </div>
                        </div>
                    )}
                    
                    {error && !isLoading && (
                        <div className="message app-message">
                            <div className="error-message">
                                <p>{error}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {!outfitData && !isLoading && !submitted && (
                <div className="event-description-container">
                    <div className="event-description-header">
                        <h3 className="event-description-title">Describe Your Event</h3>
                        <p className="event-description-subtitle">
                            Tell us about your event, including details like time of day, formality,
                            and venue type for better outfit suggestions.
                        </p>
                    </div>

                    <textarea
                        value={eventDescription}
                        onChange={(e) => setEventDescription(e.target.value)}
                        placeholder="Describe your event... (e.g., 'Wedding reception in the evening, formal dress code, outdoor venue')"
                        rows="4"
                        className="event-textarea"
                        maxLength="500"
                    />

                    <div className="character-count">
                        {eventDescription.length}/500 characters
                    </div>

                    <button 
                        className="submit-event-button"
                        onClick={handleSubmit}
                        disabled={!eventDescription.trim() || isLoading}
                    >
                        {isLoading ? (
                            <>
                                Generating suggestions
                                <div className="loading-dots">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </>
                        ) : (
                            'Get outfit suggestions'
                        )}
                    </button>

                    <div className="event-examples">
                        <p className="example-title">Quick Examples:</p>
                        <div className="example-list">
                            {examples.map((example, index) => (
                                <span
                                    key={index}
                                    className="example-tag"
                                    onClick={() => handleExampleClick(example)}
                                >
                                    {example}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            
            {outfitData && !isLoading && (
                <div className="buttons-section">
                    <div className="generate-button-container">
                        <button 
                            className="generate-outfit-btn"
                            onClick={() => {
                                setOutfitData(null);
                                setEventDescription('');
                                setSubmitted(false);
                                setIsSaved(false);
                            }}
                        >
                            Try Another Event
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventOutfit;